import "./style.css";
import { useEffect, useState, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AgGridReact } from "ag-grid-react";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";
import { Col, Row, Form } from "react-bootstrap";
import Widgets from "../../../components/Widgets";
import { useGlobalToast } from '../../../stopOldToastGlobally.js';
import {
  commonGetData,
  commonSaveData,
  initCommonState,
  initState,
} from "../../../redux/slices/commonSlice";
import {
  getEmployeeLeaveBalListPath,
  deactivateEmployeePath,
} from "../../../services/config";
import CustomPagination from "../../../components/CustomPagination";
import { FiDownload } from "react-icons/fi";
import { PiPencilSimpleLineBold } from "react-icons/pi";
import { MdClear } from 'react-icons/md';
import Button from "@mui/material/Button";
import {
  getCurrentDate,
  getInitials,
  getInitialColor,
} from "../../../services/commonServices";
import { HasPermission } from "../../../services/hasPermission";
import TableSkeleton from "../../../components/table/TableSkeleton.js";
import * as XLSX from "xlsx";

// ============================================================
// Build Excel with 2-row merged header:
//
// Row 1: | (fixed cols merged) | Leave Policy (merged) | Utilised Leaves as on {date} (merged) |
// Row 2: | Sl No | Emp ID | Name | Dept | Desig | Branch | L1 | L2 | L3 | L1 | L2 | L3 |
// Row 3+: data
// ============================================================
const downloadLeaveExcel = (dataList, customToast) => {
  if (!dataList || dataList.length === 0) {
    customToast?.warning("No data to export");  // ✅ only when empty
    return;
  }
  const today = new Date().toLocaleDateString("en-GB"); // DD/MM/YYYY

  // ── Step 1: Collect unique leave type names ──
  const leaveTypeSet = new Set();
  dataList.forEach((item) => {
    if (Array.isArray(item.leaveTypes)) {
      item.leaveTypes.forEach((lt) => {
        leaveTypeSet.add(lt.leaveName || lt.name || lt.leave_name);
      });
    } else {
      // Fallback: direct keys like item["Casual Leave"]
      const fixedKeys = [
        "staffId", "employeeId", "name", "full_name", "job_title",
        "branchName", "departmentName", "department", "status",
        "mobileNumber", "employee_id",
      ];
      Object.keys(item).forEach((key) => {
        if (!fixedKeys.includes(key) && typeof item[key] !== "object") {
          leaveTypeSet.add(key);
        }
      });
    }
  });

  const leaveTypes = Array.from(leaveTypeSet).filter(Boolean);
  const fixedCount = 6; // Sl No, Emp ID, Name, Dept, Desig, Branch
  const leaveCount = leaveTypes.length;

  // ── Step 2: Build worksheet rows ──
  const row1 = [
    "", "", "", "", "", "",                          // fixed cols (will be merged vertically in row 1+2)
    "Leave Policy", ...Array(leaveCount - 1).fill(""),  // Leave Policy group
    `Utilised Leaves as on ${today}`, ...Array(leaveCount - 1).fill(""), // Utilised group
  ];

  // ROW 2 — Column headers
  const row2 = [
    "Sl No",
    "Employee ID",
    "Employee Name",
    "Department",
    "Designation",
    "Branch",
    ...leaveTypes,   // allocated leave names
    ...leaveTypes,   // utilised leave names
  ];

  // ROW 3+ — Data rows
  const dataRows = dataList.map((item, index) => {
    const base = [
      index + 1,
      item.employeeId || item.employee_id || "-",
      item.name || item.full_name || "-",
      item.departmentName || item.department || "-",
      item.job_title || item.designation || "-",
      item.branchName || item.branch || "-",
    ];

    // Allocated values
    const allocated = leaveTypes.map((lt) => {
      if (Array.isArray(item.leaveTypes)) {
        const entry = item.leaveTypes.find(
          (x) => (x.leaveName || x.name || x.leave_name) === lt
        ) || {};
        return entry.allocated ?? entry.total ?? entry.totalLeave ?? "-";
      }
      return item[lt] ?? "-";
    });

    // Utilised values
    const utilised = leaveTypes.map((lt) => {
      if (Array.isArray(item.leaveTypes)) {
        const entry = item.leaveTypes.find(
          (x) => (x.leaveName || x.name || x.leave_name) === lt
        ) || {};
        return entry.used ?? entry.availed ?? entry.usedLeave ?? "-";
      }
      return item[`${lt}_used`] ?? "-";
    });

    return [...base, ...allocated, ...utilised];
  });

  // ── Step 3: Create worksheet from array of arrays ──
  const wsData = [row1, row2, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // ── Step 4: Merge cells ──
  const merges = [];

  // Merge fixed column headers vertically (row1 + row2, cols 0–5)
  for (let c = 0; c < fixedCount; c++) {
    merges.push({ s: { r: 0, c }, e: { r: 1, c } }); // row 0–1, col c
  }

  // Merge "Leave Policy" horizontally across leaveCount cols
  if (leaveCount > 0) {
    merges.push({
      s: { r: 0, c: fixedCount },
      e: { r: 0, c: fixedCount + leaveCount - 1 },
    });
  }

  // Merge "Utilised Leaves as on {date}" horizontally across leaveCount cols
  if (leaveCount > 0) {
    merges.push({
      s: { r: 0, c: fixedCount + leaveCount },
      e: { r: 0, c: fixedCount + leaveCount * 2 - 1 },
    });
  }

  ws["!merges"] = merges;

  // ── Step 5: Column widths ──
  ws["!cols"] = [
    { wch: 6 },   // Sl No
    { wch: 14 },  // Employee ID
    { wch: 24 },  // Employee Name
    { wch: 18 },  // Department
    { wch: 20 },  // Designation
    { wch: 16 },  // Branch
    ...Array(leaveCount * 2).fill({ wch: 18 }), // leave columns
  ];

  // ── Step 6: Write file ──
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Leave Balance");
  XLSX.writeFile(wb, `${getCurrentDate()}_Leave_Balance_Policy_Detail.xlsx`);
  customToast?.success("Leave balance details downloaded successfully!");
};

const LeaveBalancePolicyList = ({
  dispatch,
  user,
  state,
  branchList,
  designationList,
  departmentList
}) => {
  const customToast = useGlobalToast();
  const [usersList, setUsersList] = useState([]);
  const [filteredUsersList, setFilteredUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState("");

  const [jobTitle, setJobTitle] = useState("");
  const [status, setStatus] = useState(0);
  const [branch, setBranch] = useState(0);
  const [departmentId, setDepartmentId] = useState(0);

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const path = `${getEmployeeLeaveBalListPath}${user.user_id
    }&branchId=${branch}&departmentId=${departmentId}&jobTitle=${jobTitle}`;

  const getEmployeeList = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    dispatch(commonGetData(path));
  };

  useEffect(() => {
    getEmployeeList();
  }, [jobTitle, status, branch, departmentId, dispatch, path]);

  useEffect(() => {
    if (state.commonGetSuccess) {
      if (state.commonGetData == undefined || state.commonGetData == "") {
        setUsersList([]);
        setFilteredUsersList([]);
        dispatch(initState());
      } else {
        if (filterUser) {
          handleSearch(filterUser, state.commonGetData);
        } else {
          if (state.commonGetData && state.commonGetData[0].name) {
            setUsersList(state.commonGetData);
            setFilteredUsersList(state.commonGetData);
            dispatch(initState());
          }
        }
      }
    }
    if (state.commonGetError) {
      setUsersList([]);
      setFilteredUsersList([]);
      dispatch(initState());
    }
    if (state.commonSuccess) {
      getEmployeeList();
      dispatch(initCommonState());
    }
    if (state.commonError) {
      dispatch(initCommonState());
    }
  }, [state]);

  const handleActiveInactiveChange = (event, rowData) => {
    const data = {
      path: deactivateEmployeePath,
      data: {
        userId: user.user_id,
        staffId: rowData.staffId,
        isActiveUser: event.target.value,
      },
    };
    dispatch(commonSaveData(data));
  };

  const [isActive, setIsActive] = useState("");
  const handleSearchByStatus = (e, dataList) => {
    if (e.target.value) {
      setIsActive(e.target.value);
      const debouncedQuery = e.target.value;
      const results = dataList.filter(
        (employee) => employee.status == debouncedQuery
      );
      setFilteredUsersList(results);
      setUsersList(dataList);
    } else {
      setFilteredUsersList(dataList);
      setUsersList(dataList);
    }
  };

  const columns = [
    {
      headerName: "SNo",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 60,
      pinned: "left",
    },
    {
      headerName: "Employee ID",
      field: "employeeId",
      width: 120,
      pinned: "left",
    },
    {
      headerName: "Name",
      cellRenderer: (params) => {
        return (
          <Link
            to={`user-info/${params.data.staffId}`}
            className="table-link d-flex align-items-center justify-content-start gap-2"
          >
            <div
              style={{
                width: "30px",
                height: "30px",
                border: "1px solid lightgray",
                borderRadius: "50%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: getInitialColor(params.data.name),
                color: "white",
                fontWeight: "bold",
                fontSize: "12px",
              }}
            >
              {getInitials(params.data.name)}
            </div>
            {params.data.name}
          </Link>
        );
      },
      pinned: "left",
      flex: 1,
      width: "350px",
    },
    {
      headerName: "Job Title",
      field: "job_title",
      width: "250px",
    },
    {
      headerName: "Branch",
      field: "branchName",
      width: "200px",
      autoHeight: true,
      wrapText: true,
      resizable: true,
      cellClass: "custom-wrap-cell",
    },
    {
      headerName: "Casual Leave",
      cellRenderer: (rowData) => {
        return rowData.data["Casual Leave"];
      },
      width: "150px",
    },
    {
      headerName: "Sick Leave",
      cellRenderer: (rowData) => {
        return rowData.data["Sick Leave"];
      },
      width: "150px",
    },
    {
      headerName: "Other Leave",
      cellRenderer: (rowData) => {
        return rowData.data["Other Leave"];
      },
      width: "150px",
    },
    {
      headerName: "Action",
      pinned: "right",
      width: "150px",
      cellRenderer: (rowData) => {
        return (
          <div
            style={{ position: "relative", top: "9px" }}
            className="d-flex align-items-center justify-content-start gap-3"
          >
            <select
              className={
                rowData.data.status == "1" ? "statusGreen" : "statusOrange"
              }
              value={rowData.data.status}
              onChange={(e) => handleActiveInactiveChange(e, rowData.data)}
            >
              <option
                value="1"
                selected={rowData.data.status == "1" && "selected"}
              >
                Active
              </option>
              <option
                value="0"
                selected={rowData.data.status != "1" && "selected"}
              >
                InActive
              </option>
            </select>
            <Link
              to={`user-info/${rowData.data.staffId}`}
              className="table-link d-flex align-items-center justify-content-start gap-2"
              style={{ fontSize: "24px" }}
            >
              <PiPencilSimpleLineBold className="edit-icon" />
            </Link>
          </div>
        );
      },
    },
  ];

  const handleSearch = useCallback(
    debounce((query, dataList) => {
      const debouncedQuery = query.trim().toLowerCase();
      const results = dataList.filter(
        (employee) =>
          (employee.name &&
            employee.name.toLowerCase().includes(debouncedQuery)) ||
          (employee.job_title &&
            employee.job_title.toLowerCase().includes(debouncedQuery)) ||
          (employee.employeeId &&
            employee.employeeId.toLowerCase().includes(debouncedQuery))
      );
      setFilteredUsersList(results);
      setUsersList(usersList);
    }, 500),
    [usersList]
  );

  const searchUser = (event) => {
    const { target: { value } } = event;
    setFilterUser(value);
    handleSearch(value, usersList);
  };

  // ── Updated download: Excel with 2-row merged header ──
 const downloadBulkData = () => {
  if (!filteredUsersList || filteredUsersList.length === 0) {
    customToast.warning("No data to export");  // ✅
    return;
  }
  downloadLeaveExcel(filteredUsersList, customToast);  // ✅ pass customToast
};

  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 20, 50, 100];

  const clearFilter = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    getEmployeeList();
    setFilterUser("");
    setJobTitle("");
    setDepartmentId(0);
    setBranch(0);
  };

  return (
    <div>
      <div className="search-box flex-space-between mt-m-40">
        <Form className="d-flex align-items-center justify-content-start gap-2">
          <Form.Group>
            <Form.Control
              placeholder="Search by Employee Name & ID"
              onChange={searchUser}
              name="filterUser"
              value={filterUser}
              className="universal-input-one"
              style={{ width: "250px" }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              onChange={(e) => setJobTitle(e.target.value)}
              name="jobTitle"
              value={jobTitle}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Job Title</option>
              {designationList?.map((row) => {
                return <option value={row.label} key={row.label}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setBranch(e.target.value)}
              name="branch"
              value={branch}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Branch</option>
              {branchList?.map((row) => {
                return <option value={row.branchId} key={row.branchId}>{row.branch_name}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setDepartmentId(e.target.value)}
              name="departmentId"
              value={departmentId}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="0">Select Department</option>
              {departmentList?.map((row) => {
                return <option value={row.value} key={row.value}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => handleSearchByStatus(e, usersList)}
              className="universal-input-one"
              style={{ width: "140px" }}
            >
              <option value="">Select Status</option>
              <option value="1">Active</option>
              <option value="0">InActive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            {
              (filterUser ||
                jobTitle ||
                departmentId != 0 ||
                branch != 0 || isActive) && <button type="button" className="clear" onClick={clearFilter}>
                <MdClear /> Clear
              </button>
            }
          </Form.Group>
        </Form>
      </div>

      <Widgets
        body={
          <>
            <div
              className="ag-theme-quartz custom-grid"
              style={{ height: "calc(100vh - 310px)", width: "100%" }}
            >
              {state.commonGetLoading ? (
                <TableSkeleton />
              ) : (
                <AgGridReact
                  rowData={Array.isArray(filteredUsersList) ? filteredUsersList : []}
                  columnDefs={columns}
                  omLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                  onGridReady={onGridReady}
                />
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between custom-pagination">
              {gridApi && <CustomPagination gridApi={gridApi} />}
              {HasPermission("Leave Balance & Policy", "export") && (
                <Button
                  onClick={downloadBulkData}
                  className="bulkDataDownloads universal-btn"
                >
                  <FiDownload style={{ fontSize: "16px", marginRight: '10px' }} />
                  Export Data
                </Button>
              )}
            </div>
          </>
        }
      />
    </div>
  );
};

export default LeaveBalancePolicyList;